import { AIContext } from '@/apiTypes/AiTypes';

const STORAGE_KEY = 'asa-ai-context-persisted';

/**
 * Get persisted AI context from sessionStorage.
 * Returns empty object if storage is unavailable or data is invalid.
 */
export const getPersistedContext = (): AIContext => {
    try {
        const stored = sessionStorage.getItem(STORAGE_KEY);
        if (!stored) {
            return {};
        }
        const parsed = JSON.parse(stored);
        if (typeof parsed !== 'object' || parsed === null) {
            return {};
        }
        return parsed as AIContext;
    }
    catch (error) {
        console.error('Error reading persisted context', error);
        return {};
    }
};

/**
 * Set persisted AI context in sessionStorage.
 * Only persists the specified keys from the context.
 */
export const setPersistedContext = (context: AIContext, keys: string[]): void => {
    try {
        const existing = getPersistedContext();
        const updated: AIContext = { ...existing };

        for (const key of keys) {
            if (key in context) {
                updated[key] = context[key];
            }
        }

        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    }
    catch (error) {
        console.error('Error writing persisted context', error);
    }
};

/**
 * Remove specific keys from persisted context.
 */
export const removePersistedKeys = (keys: string[]): void => {
    try {
        const existing = getPersistedContext();
        const updated: AIContext = { ...existing };

        for (const key of keys) {
            delete updated[key];
        }

        if (Object.keys(updated).length === 0) {
            sessionStorage.removeItem(STORAGE_KEY);
        }
        else {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
        }
    }
    catch (error) {
        console.error('Error removing persisted context keys', error);
    }
};
